import React, { useState, useEffect } from 'react';
import { Clock, ExternalLink } from 'lucide-react';

interface ShiftCountdownProps {
    tierLabel: string;
}

export const ShiftCountdown: React.FC<ShiftCountdownProps> = ({ tierLabel }) => {
    const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number } | null>(null);
    const [isShiftOpen, setIsShiftOpen] = useState(false);

    useEffect(() => {
        // Target Schedule Rules based on Tier
        // Ouro: Monday (1) 16:00
        // Prata: Tuesday (2) 12:00
        // Bronze: Tuesday (2) 16:00
        let targetDay = 2;
        let targetHour = 6;
        let targetMinute = 0;

        if (tierLabel === 'Ouro') {
            targetDay = 1;
            targetHour = 16;
        }

        const calculateTimeLeft = () => {
            const now = new Date();
            const currentDay = now.getDay();

            // If it's the target day, and time is past targetHour:targetMinute, the shift is OPEN until midnight.
            if (currentDay === targetDay) {
                const isPastTime = now.getHours() > targetHour || (now.getHours() === targetHour && now.getMinutes() >= targetMinute);
                if (isPastTime) {
                    setIsShiftOpen(true);
                    setTimeLeft(null);
                    return;
                }
            }

            // Not open yet, calculate remaining time
            setIsShiftOpen(false);

            let target = new Date(now);
            target.setHours(targetHour, targetMinute, 0, 0);

            let daysToAdd = targetDay - currentDay;

            // If the time has already passed today or the target day is in the past this week
            if (daysToAdd < 0 || (daysToAdd === 0 && now.getTime() > target.getTime())) {
                daysToAdd += 7; // Next week
            }

            target.setDate(now.getDate() + daysToAdd);

            const diffMs = target.getTime() - now.getTime();
            if (diffMs <= 0) {
                setIsShiftOpen(true);
                setTimeLeft(null);
                return;
            }

            const totalMinutes = Math.floor(diffMs / (1000 * 60));
            setTimeLeft({
                hours: Math.floor(totalMinutes / 60),
                minutes: totalMinutes % 60
            });
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 60000); // update every minute

        return () => clearInterval(timer);
    }, [tierLabel]);

    // Until initial calculation is done, we can show nothing to avoid layout shifts
    if (!timeLeft && !isShiftOpen) return null;

    return (
        <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${tierLabel === 'Ouro' ? 'bg-yellow-500/20 text-yellow-500' : tierLabel === 'Prata' ? 'bg-slate-300/20 text-slate-300' : 'bg-amber-900/40 text-amber-500'}`}>
                    <Clock className="w-6 h-6" />
                </div>
                <div>
                    <h4 className="text-white font-bold leading-tight flex items-center gap-2">
                        Abertura de Escala
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-black ${tierLabel === 'Ouro' ? 'bg-yellow-500/20 text-yellow-400' :
                            tierLabel === 'Prata' ? 'bg-slate-500/20 text-slate-300' :
                                'bg-amber-900/40 text-amber-500'
                            }`}>
                            {tierLabel}
                        </span>
                    </h4>
                    <p className="text-sm text-slate-400 mt-0.5">
                        {tierLabel === 'Ouro' ? 'Segunda-feira às 16:00' : 'Terça-feira às 06:00'}
                    </p>
                </div>
            </div>

            <div className="text-right w-full md:w-auto">
                {isShiftOpen ? (
                    <a
                        href="https://system.go-rhino.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white font-black px-6 py-3 rounded-xl transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] animate-pulse w-full md:w-auto justify-center"
                    >
                        ACESSAR ESCALA RHINO
                        <ExternalLink className="w-5 h-5" />
                    </a>
                ) : (
                    <>
                        <div className="text-2xl font-black text-white font-mono flex items-baseline gap-1 justify-end">
                            {timeLeft?.hours}<span className="text-sm text-slate-500 font-sans mr-1">h</span>
                            {timeLeft?.minutes.toString().padStart(2, '0')}<span className="text-sm text-slate-500 font-sans">m</span>
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mt-1">Restantes</div>
                    </>
                )}
            </div>
        </div >
    );
};
